import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@/modes/waterSupplier/gql';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label, Select } from '../Form';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import { ucFirst } from '@ecosconnect/core/src/helpers/string';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import { Textarea } from '@rebass/forms';
import { HazardLevel, HazardLevelPicker } from './HazardLevelPicker';

export const waterHazardFragment = gql(`
  fragment WaterHazardFields on WaterHazard {
    id
    name
    description
    hazardLevel
  }
`);

export type WaterHazardInput = {
  name: string;
  description: string;
  hazardLevel: 'HIGH' | 'LOW';
};

type WaterHazardEditModalProps = {
  onClose: () => void;
  waterHazard?: Partial<WaterHazardInput>;
  waterHazardId?: string;
} & (
  | {
      waterHazardId: string;
      action: 'edit';
    }
  | {
      action: 'add';
    }
);

const CREATE_WATER_HAZARD = gql(
  `
  mutation CreateWaterHazard($input: WaterHazardInput!) {
    createWaterHazard(input: $input) {
      ...WaterHazardFields
    }
  }
`,
  [waterHazardFragment]
);

const UPDATE_WATER_HAZARD = gql(
  `
  mutation UpdateWaterHazard($waterHazardId: ID!, $input: WaterHazardInput!) {
    updateWaterHazard(waterHazardId: $waterHazardId, input: $input) {
      ...WaterHazardFields
    }
  }
`,
  [waterHazardFragment]
);

const WaterHazardEditModal: React.FC<WaterHazardEditModalProps> = ({
  onClose,
  waterHazard,
  waterHazardId,
  action,
}) => {
  const [waterHazardInput, setWaterHazardInput] = useState<WaterHazardInput>({
    name: waterHazard?.name ?? '',
    description: waterHazard?.description ?? '',
    hazardLevel: waterHazard?.hazardLevel ?? ('' as any),
  });

  const { showError, showSuccess } = useNotify();

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    updateField(name, value);
  };

  const updateField = <F extends keyof WaterHazardInput>(
    name: F,
    value?: WaterHazardInput[F]
  ) => {
    setWaterHazardInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onCompleted = () => {
    showSuccess('Water hazard saved');
    onClose();
  };

  const onError = (error: ApolloError) => {
    showError('Error saving water hazard', error);
  };

  const [
    saveWaterHazard,
    { data: saveData, loading: saveLoading, error: saveError },
  ] =
    action === 'edit'
      ? useMutation(UPDATE_WATER_HAZARD, {
          onCompleted,
          onError,
        })
      : useMutation(CREATE_WATER_HAZARD, {
          onCompleted,
          onError,
        });

  const save = useCallback(async () => {
    if (!waterHazardInput.name) {
      showError('Please enter a name');
      return;
    }
    if (!waterHazardInput.description) {
      showError('Please enter a description');
      return;
    }
    if (!waterHazardInput.hazardLevel) {
      showError('Please select a hazard level');
      return;
    }
    if (action === 'edit' && !waterHazardId) {
      showError('No water hazard ID provided');
      return;
    }
    await (saveWaterHazard as any)({
      variables: { waterHazardId, input: waterHazardInput },
    });
  }, [waterHazardId, waterHazardInput]);

  return (
    <Modal onClose={onClose} title={`${ucFirst(action)} Water Hazard`}>
      <ModalBody>
        <Form>
          <FormColumn>
            <Label>Name</Label>
            <Input
              width="100%"
              type="text"
              autoFocus
              value={waterHazardInput.name}
              onChange={handleChange}
              name="name"
              placeholder="Name"
              autoComplete="off"
            />
          </FormColumn>
          <FormColumn>
            <Label>Hazard Level</Label>
            <HazardLevelPicker
              value={waterHazardInput.hazardLevel}
              onChange={(value) => {
                setWaterHazardInput((prevState) => ({
                  ...prevState,
                  hazardLevel: value as HazardLevel,
                }));
              }}
            />
          </FormColumn>
          <FormColumn>
            <Label>Description</Label>
            <Textarea
              width="100%"
              height={69}
              value={waterHazardInput.description}
              onChange={handleChange}
              name="description"
              placeholder="Description"
              autoComplete="off"
            />
          </FormColumn>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" disabled={saveLoading} onClick={save}>
          Save
        </Button>
        <Button ml={3} variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WaterHazardEditModal;
